import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    useSelector } from 'react-redux';
import {
  getAuthSession,
  updateSessionState,
  KITCHEN_ORDER,
  KITCHEN_ORDER_STATUS_UPDATE,
  SUCCESS
} from '../../constants/constant';
import { snpAxios } from '../global/api';
import DialogBox from '../global/DialogBox';
import './KitchenUI.css';

const SEND_TO_KITCHEN = 'Send to Kitchen';
const KITCHEN_COOKING = 'Cooking';
const KITCHEN_READY = 'Ready';
const AUTO_OPEN_CONFIG = 'Open_Kitchen_After_Login';

const getBills = (response) => response?.data?.jcJson?.kitchenBills || [];

const getAggregatedItems = (bills) => {
  const itemTotals = new Map();

  bills.forEach((bill) => {
    (bill.items || []).forEach((item) => {
      const itemName = item.itemName?.trim();
      if (!itemName) return;

      const key = itemName.toLocaleLowerCase();
      const quantity = Number(item.itemQuantity) || 0;
      const signedQuantity = item.positive === false ? -quantity : quantity;
      const current = itemTotals.get(key);

      itemTotals.set(key, {
        itemName: current?.itemName || itemName,
        quantity: (current?.quantity || 0) + signedQuantity,
      });
    });
  });

  return [...itemTotals.values()]
    .filter((item) => item.quantity > 0)
    .sort((first, second) => first.itemName.localeCompare(second.itemName));
};

export default function KitchenUI() {
  const storeActiveShopHash = useSelector((state) => state.setActiveShopHashReducer.activeShopHash);
  const storeApiUrl = useSelector((state) => state.setApiUrlReducer.MAINURL.INITIAL_BASE_URL);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingBill, setUpdatingBill] = useState('');
  const [error, setError] = useState('');
  const [autoOpen, setAutoOpen] = useState(getAuthSession().openKitchenAfterLogin);
  const [summaryDialog, setSummaryDialog] = useState(null);
  const baseUrl = storeApiUrl.BACKEND_BASE_URL;
  const shopHash = storeActiveShopHash.id;

  const mergeBill = useCallback((bill) => {
    if (!bill?.billNo) return;
    setOrders((current) => [bill, ...current.filter((item) => item.billNo !== bill.billNo)]);
  }, []);

  const fetchBill = useCallback(async (billNo) => {
    if (!billNo || !shopHash) return;
    try {
      const response = await snpAxios.get(`${baseUrl}billing/get-kitchen-bill-by-bill`, {
        params: { token: getAuthSession().apiToken, shopHash, billNo },
      });
      getBills(response).forEach(mergeBill);
    } catch (requestError) {
      console.error('Unable to refresh kitchen order', requestError);
    }
  }, [baseUrl, mergeBill, shopHash]);

  const loadKitchen = useCallback(async () => {
    if (!shopHash) return;
    setLoading(true);
    setError('');
    try {
      const request = (kitchenStatus) => snpAxios.get(`${baseUrl}billing/get-kitchen-bill-by-status`, {
        params: { token: getAuthSession().apiToken, shopHash, kitchenStatus },
      });
      const [newResponse, cookingResponse] = await Promise.all([
        request(SEND_TO_KITCHEN),
        request(KITCHEN_COOKING),
      ]);
      setOrders([...getBills(newResponse), ...getBills(cookingResponse)]);
    } catch (requestError) {
      console.error('Unable to load kitchen orders', requestError);
      setError('Kitchen orders could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [baseUrl, shopHash]);

  useEffect(() => {
    loadKitchen();
  }, [loadKitchen]);

  useEffect(() => {
    const handleKitchenNotification = (event) => {
      const notification = event.detail || {};
      if (notification.status === KITCHEN_ORDER) {
        fetchBill(notification.billNo);
        return;
      }
      if (notification.status === KITCHEN_ORDER_STATUS_UPDATE) {
        setOrders((current) => {
          const existing = current.find((bill) => bill.billNo === notification.billNo);
          if (!existing) return current;
          if (notification.kitchenStatus === KITCHEN_READY) {
            return current.filter((bill) => bill.billNo !== notification.billNo);
          }
          return current.map((bill) => bill.billNo === notification.billNo
            ? { ...bill, kitchenStatus: notification.kitchenStatus }
            : bill);
        });
      }
    };
    window.addEventListener('snp-kitchen-notification', handleKitchenNotification);
    return () => window.removeEventListener('snp-kitchen-notification', handleKitchenNotification);
  }, [fetchBill]);

  const updateStatus = async (billNo, kitchenStatus) => {
    setUpdatingBill(billNo);
    setError('');
    try {
      const response = await snpAxios.patch(`${baseUrl}billing/update-kitchen-bill-status`, {
        jcJson: {
          apiToken: getAuthSession().apiToken,
          shopHash,
          billNo,
          kitchenStatus,
        },
      });
      if (response.data.status !== SUCCESS) throw new Error('Kitchen status update failed');
      setOrders((current) => kitchenStatus === KITCHEN_READY
        ? current.filter((bill) => bill.billNo !== billNo)
        : current.map((bill) => bill.billNo === billNo ? { ...bill, kitchenStatus } : bill));
    } catch (requestError) {
      console.error('Unable to update kitchen order', requestError);
      setError('The order status was not changed. Please try again.');
    } finally {
      setUpdatingBill('');
    }
  };

  const updateAutoOpen = async (enabled) => {
    const previous = autoOpen;
    setAutoOpen(enabled);
    try {
      const configResponse = await snpAxios.get(`${baseUrl}resto/get-single-full-configs`, {
        params: {
          adminId: getAuthSession().apiToken,
          configName: AUTO_OPEN_CONFIG,
          shopHash,
          prefix: '',
        },
      });
      const config = configResponse.data?.jcJson;
      if (!config?.id) throw new Error('Kitchen configuration is missing');
      const response = await snpAxios.post(`${baseUrl}resto/persist-configs`, {
        jcJson: {
          apiToken: getAuthSession().apiToken,
          shopHash,
          id: config.id,
          configName: AUTO_OPEN_CONFIG,
          prefix: config.prefix || '',
          comments: config.comments || '',
          val: enabled ? 'Yes' : 'No',
          allowed: config.allowed ?? true,
        },
      });
      if (response.data.status !== SUCCESS) throw new Error('Kitchen configuration update failed');
      updateSessionState({ openKitchenAfterLogin: enabled });
    } catch (requestError) {
      console.error('Unable to save kitchen preference', requestError);
      setAutoOpen(previous);
      setError('The automatic-open preference could not be saved.');
    }
  };

  const newOrders = useMemo(() => orders.filter((bill) => bill.kitchenStatus === SEND_TO_KITCHEN), [orders]);
  const cookingOrders = useMemo(() => orders.filter((bill) => bill.kitchenStatus === KITCHEN_COOKING), [orders]);

  const summaryItems = useMemo(
    () => getAggregatedItems(summaryDialog?.bills || []),
    [summaryDialog],
  );

  const renderOrder = (bill, actionLabel, nextStatus) => (
    <article className="kitchen-card" key={bill.billNo}>
      <header className="kitchen-card__header">
        <div>
          <span className="kitchen-card__eyebrow">Order {bill.billNo}</span>
          <h3>{bill.name || 'Kitchen order'}</h3>
        </div>
        <div className="kitchen-card__meta">
          {bill.tokenNo ? <strong>Token {bill.tokenNo}</strong> : null}
          <span>{bill.billCreateAt}</span>
        </div>
      </header>
      <ul className="kitchen-card__items">
        {(bill.items || []).map((item, index) => (
          <li className={!item.positive ? 'kitchen-card__item--removed' : ''} key={`${item.itemName}-${index}`}>
            <span>{item.itemName}</span>
            <strong>{item.positive ? '' : '−'}{item.itemQuantity}</strong>
          </li>
        ))}
      </ul>
      <button
        className={`kitchen-card__action kitchen-card__action--${nextStatus === KITCHEN_READY ? 'ready' : 'cook'}`}
        disabled={updatingBill === bill.billNo}
        onClick={() => updateStatus(bill.billNo, nextStatus)}
      >
        {updatingBill === bill.billNo ? 'Updating…' : actionLabel}
      </button>
    </article>
  );

  const renderLane = (title, subtitle, bills, actionLabel, nextStatus, className) => (
    <section className={`kitchen-lane ${className}`}>
      <div className="kitchen-lane__title">
        <div><h2>{title}</h2><p>{subtitle}</p></div>
        <div className="kitchen-lane__tools">
          <button
            className="kitchen-lane__summary-button"
            onClick={() => setSummaryDialog({ title, bills })}
            type="button"
          >
            Item summary
          </button>
          <span className="kitchen-lane__count">{bills.length}</span>
        </div>
      </div>
      <div className="kitchen-lane__orders">
        {bills.length ? bills.map((bill) => renderOrder(bill, actionLabel, nextStatus)) : (
          <div className="kitchen-empty">No orders here right now.</div>
        )}
      </div>
    </section>
  );

  return (
    <div className="kitchen-page">
      <div className="kitchen-page__topbar">
        <div><span className="kitchen-page__label">Live kitchen</span><h1>Kitchen display</h1></div>
        <div className="kitchen-page__controls">
          <label className="kitchen-auto-open">
            <input type="checkbox" checked={autoOpen} onChange={(event) => updateAutoOpen(event.target.checked)} />
            <span>Open automatically after login</span>
          </label>
          <button className="kitchen-refresh" onClick={loadKitchen}>Refresh</button>
        </div>
      </div>
      {error ? <div className="kitchen-error">{error}</div> : null}
      {loading ? <div className="kitchen-loading">Loading kitchen orders…</div> : (
        <div className="kitchen-board">
          {renderLane('New orders', 'Waiting to be started', newOrders, 'Start cooking', KITCHEN_COOKING, 'kitchen-lane--new')}
          {renderLane('In cooking', 'Being prepared now', cookingOrders, 'Mark ready', KITCHEN_READY, 'kitchen-lane--cooking')}
        </div>
      )}
      <DialogBox
        showDialog={Boolean(summaryDialog)}
        title={`${summaryDialog?.title || 'Kitchen'} item summary`}
        onClose={() => setSummaryDialog(null)}
        mwidth="sm"
        mFullWidth
        dialogContent={(
          <div className="kitchen-summary">
            <p className="kitchen-summary__description">
              Combined quantities from {summaryDialog?.bills?.length || 0} active orders
            </p>
            {summaryItems.length ? (
              <ul className="kitchen-summary__items">
                {summaryItems.map((item) => (
                  <li key={item.itemName.toLocaleLowerCase()}>
                    <span>{item.itemName}</span>
                    <strong>{item.quantity}</strong>
                  </li>
                ))}
              </ul>
            ) : <div className="kitchen-summary__empty">No active items in this section.</div>}
          </div>
        )}
        dialogActions={(
          <button className="kitchen-summary__close" onClick={() => setSummaryDialog(null)} type="button">
            Close
          </button>
        )}
      />
    </div>
  );
}
